// In-app replacements for native `window.confirm` / `window.prompt`. Themed
// to match the rest of the admin UI, keyboard accessible (Esc cancels,
// Enter confirms), and focus-trapped while open.
//
// Drop-in semantics:
//   const ok = await confirmModal({ title: 'Delete?' });
//   if (!ok) return;
//
//   const name = await promptModal({ title: 'New name?' });
//   if (name === null) return;        // cancelled
//
// Cancel resolves to `false` for confirm, `null` for prompt — matching the
// native APIs so callers can flip from `confirm(...)` to `await confirmModal(...)`
// with minimal diff.

export interface ConfirmModalOptions {
  title: string;
  /** Optional secondary body text. Plain string — escaped on render. */
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). */
  danger?: boolean;
}

export interface PromptModalOptions {
  title: string;
  body?: string;
  defaultValue?: string;
  placeholder?: string;
  inputType?: 'text' | 'password' | 'number';
  confirmLabel?: string;
  cancelLabel?: string;
}

let modalRoot: HTMLElement | null = null;

function ensureRoot(): HTMLElement {
  if (modalRoot && modalRoot.isConnected) return modalRoot;
  const el = document.createElement('div');
  el.id = 'modal-root';
  document.body.appendChild(el);
  modalRoot = el;
  return el;
}

function openModal(content: HTMLElement, onClose: () => void): () => void {
  const root = ensureRoot();
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.appendChild(content);
  root.appendChild(backdrop);

  // Lock background scroll while open.
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const close = (): void => {
    document.body.style.overflow = prevOverflow;
    backdrop.removeEventListener('mousedown', backdropClick);
    document.removeEventListener('keydown', keyHandler, true);
    backdrop.remove();
    onClose();
  };

  const backdropClick = (ev: MouseEvent): void => {
    if (ev.target === backdrop) close();
  };
  backdrop.addEventListener('mousedown', backdropClick);

  const keyHandler = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      close();
    }
  };
  document.addEventListener('keydown', keyHandler, true);

  return close;
}

export function confirmModal(opts: ConfirmModalOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const card = document.createElement('div');
    card.className = 'modal-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.innerHTML = `
      <h2 class="modal-title">${escapeHtml(opts.title)}</h2>
      ${opts.body ? `<p class="modal-body">${escapeHtml(opts.body)}</p>` : ''}
      <div class="modal-actions">
        <button type="button" class="ghost modal-cancel">${escapeHtml(opts.cancelLabel ?? 'Cancel')}</button>
        <button type="button" class="${opts.danger ? 'danger' : 'primary'} modal-confirm">${escapeHtml(opts.confirmLabel ?? 'Confirm')}</button>
      </div>
    `;

    let settled = false;
    const settle = (val: boolean): void => {
      if (settled) return;
      settled = true;
      close();
      resolve(val);
    };

    const close = openModal(card, () => settle(false));
    const confirmBtn = card.querySelector<HTMLButtonElement>('.modal-confirm')!;
    const cancelBtn = card.querySelector<HTMLButtonElement>('.modal-cancel')!;
    confirmBtn.addEventListener('click', () => settle(true));
    cancelBtn.addEventListener('click', () => settle(false));
    card.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        settle(true);
      }
    });
    setTimeout(() => confirmBtn.focus(), 0);
  });
}

export function promptModal(opts: PromptModalOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const card = document.createElement('div');
    card.className = 'modal-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    const inputType = opts.inputType ?? 'text';
    card.innerHTML = `
      <h2 class="modal-title">${escapeHtml(opts.title)}</h2>
      ${opts.body ? `<p class="modal-body">${escapeHtml(opts.body)}</p>` : ''}
      <input class="modal-input" type="${inputType}" placeholder="${escapeAttr(opts.placeholder ?? '')}" value="${escapeAttr(opts.defaultValue ?? '')}" />
      <div class="modal-actions">
        <button type="button" class="ghost modal-cancel">${escapeHtml(opts.cancelLabel ?? 'Cancel')}</button>
        <button type="button" class="primary modal-confirm">${escapeHtml(opts.confirmLabel ?? 'OK')}</button>
      </div>
    `;

    let settled = false;
    const settle = (val: string | null): void => {
      if (settled) return;
      settled = true;
      close();
      resolve(val);
    };

    const close = openModal(card, () => settle(null));
    const input = card.querySelector<HTMLInputElement>('.modal-input')!;
    const confirmBtn = card.querySelector<HTMLButtonElement>('.modal-confirm')!;
    const cancelBtn = card.querySelector<HTMLButtonElement>('.modal-cancel')!;
    confirmBtn.addEventListener('click', () => settle(input.value));
    cancelBtn.addEventListener('click', () => settle(null));
    input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        settle(input.value);
      }
    });
    setTimeout(() => {
      input.focus();
      input.select();
    }, 0);
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/**
 * Trusted-HTML marker produced ONLY by the `html` tagged template. `customModal`
 * accepts this instead of a raw string so there is no way to feed it an
 * unescaped value by accident — the previous `bodyHtml: string` was a stored-XSS
 * sink waiting for a caller to forget `escapeHtml` (C12).
 */
const TRUSTED = Symbol('trusted-html');
export interface TrustedHtml {
  readonly [TRUSTED]: true;
  readonly value: string;
}

/**
 * lit-style tagged template. Every `${}` interpolation is HTML-escaped by
 * default; a nested `TrustedHtml` (built by `html` itself) is spliced in
 * verbatim so fragments compose. Static template text is trusted by construction.
 */
function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(renderValue).join('');
  if (typeof v === 'object' && (v as Partial<TrustedHtml>)[TRUSTED] === true) {
    return (v as TrustedHtml).value; // nested fragment — already escaped
  }
  return escapeHtml(String(v));
}

export function html(strings: TemplateStringsArray, ...values: unknown[]): TrustedHtml {
  let out = strings[0] ?? '';
  for (let i = 0; i < values.length; i++) {
    out += renderValue(values[i]) + (strings[i + 1] ?? '');
  }
  return { [TRUSTED]: true, value: out };
}

/**
 * String-arg shim for `window.confirm`. Lets call sites swap from
 * `if (!confirm(msg)) return;` to `if (!(await askConfirm(msg))) return;`
 * with a one-token diff.
 */
export function askConfirm(message: string, opts: { danger?: boolean } = {}): Promise<boolean> {
  return confirmModal({ title: message, danger: opts.danger });
}

/** String-arg shim for `window.prompt`. Resolves to null on cancel. */
export function askPrompt(message: string, defaultValue?: string): Promise<string | null> {
  return promptModal({ title: message, defaultValue });
}

export interface InfoModalOptions {
  title: string;
  /** Body markup, built with the `html` tagged template (auto-escaping). */
  body: TrustedHtml;
  /** Optional subtitle shown under the title (plain text, escaped). */
  subtitle?: string;
  closeLabel?: string;
  /** Widen the card for dense detail grids (default false). */
  wide?: boolean;
}

/**
 * Read-only detail modal — a single "Close" affordance, no confirm/cancel
 * branching. For drill-ins that show a record's full detail (audit entries,
 * chain heads, wallet calls) without asking the user to decide anything.
 */
export function infoModal(opts: InfoModalOptions): () => void {
  const card = document.createElement('div');
  card.className = `modal-card${opts.wide ? ' modal-card-wide' : ''}`;
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.innerHTML = `
    <h2 class="modal-title">${escapeHtml(opts.title)}</h2>
    ${opts.subtitle ? `<p class="modal-body muted" style="margin-top:-6px">${escapeHtml(opts.subtitle)}</p>` : ''}
    <div class="modal-body">${opts.body.value}</div>
    <div class="modal-actions">
      <button type="button" class="primary" data-modal-close>${escapeHtml(opts.closeLabel ?? 'Close')}</button>
    </div>
  `;
  const close = openModal(card, () => {});
  card.querySelector<HTMLButtonElement>('[data-modal-close]')?.addEventListener('click', () => close());
  return close;
}

export interface CustomModalOptions {
  title: string;
  /** Body markup, built with the `html` tagged template (auto-escaping). A
   *  raw string is no longer accepted — interpolations are escaped by
   *  construction, so this is not an XSS sink. */
  body: TrustedHtml;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Called with the dialog root. Return parsed value or null to cancel
   *  confirmation (e.g. validation failed). */
  parseOnConfirm: (root: HTMLElement) => unknown;
}

/**
 * Generic single-shot modal for forms that don't fit promptModal's single
 * input. Caller controls the markup; we wire confirm/cancel + ESC + backdrop
 * close. Resolves to the parser's return value, or null on cancel.
 */
export function customModal<T = unknown>(opts: CustomModalOptions): Promise<T | null> {
  return new Promise((resolve) => {
    const card = document.createElement('div');
    card.className = 'modal-card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.innerHTML = `
      <h2 class="modal-title">${escapeHtml(opts.title)}</h2>
      <div class="modal-body">${opts.body.value}</div>
      <div class="modal-actions">
        <button type="button" class="ghost" data-modal-cancel>${escapeHtml(opts.cancelLabel ?? 'Cancel')}</button>
        <button type="button" class="${opts.danger ? 'danger' : 'primary'}" data-modal-confirm>${escapeHtml(opts.confirmLabel ?? 'Confirm')}</button>
      </div>
    `;
    let settled = false;
    const close = openModal(card, () => {
      if (!settled) {
        settled = true;
        resolve(null);
      }
    });
    card.querySelector<HTMLButtonElement>('[data-modal-cancel]')?.addEventListener('click', () => {
      settled = true;
      resolve(null);
      close();
    });
    card.querySelector<HTMLButtonElement>('[data-modal-confirm]')?.addEventListener('click', () => {
      const parsed = opts.parseOnConfirm(card);
      if (parsed === null || parsed === undefined) return; // validation failed; keep open
      settled = true;
      resolve(parsed as T);
      close();
    });
  });
}
